import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { BackendResponse } from '../types';
import { jwtDecode } from 'jwt-decode';
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Leaf,
  ArrowLeft,
  RefreshCw,
  Shield,
  Clock,
  Check
} from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const VerificationPendingPage: React.FC = () => {
  const { t, isRTL } = useI18n();

  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [resendError, setResendError] = useState<string>('');

  useEffect(() => {
    // Get email from navigation state
    const state = location.state as { email: string; message: string } | null;
    if (state?.email) {
      setEmail(state.email);
      setMessage(state.message);
    } else {
      // If no email in state, try to get it from the JWT token
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decoded = jwtDecode(token) as any;
          if (decoded.sub) {
            setEmail(decoded.sub);
            setMessage(t('auth.verificationPending.pendingMessage'));
            return;
          }
        } catch (error) {
          console.error('Error decoding token:', error);
        }
      }
      // If no email found, redirect to signup
      navigate('/signup');
    }
  }, [location.state, navigate]);

  const handleResendVerification = async (): Promise<void> => {
    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const response: BackendResponse = await authAPI.resendVerification(email);
      setResendSuccess(true);
      setMessage(response.message || t('auth.verificationPending.resendSuccess'));
    } catch (error: unknown) {
      const apiError = error as any;
      setResendError(apiError.response?.data?.message || t('auth.verificationPending.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoToLogin = (): void => {
    navigate('/login');
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 spinner mx-auto mb-4"></div>
          <p className="text-secondary-600">{t('auth.verificationPending.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="max-w-md text-center text-white px-8">
            <Link to="/" className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                <img src="/solvingers.svg" />
              </div>
              <h1 className="text-4xl font-bold">Facturelik</h1>
            </Link>

            <h2 className="text-3xl font-semibold mb-4">{t('auth.verificationPending.pageTitle')}</h2>
            <p className="text-lg text-white/90 mb-12">
              {t('auth.verificationPending.pageSubtitle')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.verificationPending.checkEmailTitle')}</h3>
                  <p className="text-white/80">{t('auth.verificationPending.checkEmailDesc')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.verificationPending.requiredTitle')}</h3>
                  <p className="text-white/80">{t('auth.verificationPending.requiredDesc')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.verificationPending.secureTitle')}</h3>
                  <p className="text-white/80">{t('auth.verificationPending.secureDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-3">
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">Facturelik</h1>
          </Link>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-warning-600" />
            </div>
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('auth.verificationPending.mainTitle')}</h2>
            <p className="text-secondary-600">{t('auth.verificationPending.mainSubtitle')}</p>
          </div>

          {message && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
              <p className="text-success-800">{message}</p>
            </div>
          )}

          {resendSuccess && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
              <p className="text-success-800">{t('auth.verificationPending.resendSuccess')}</p>
            </div>
          )}

          {resendError && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <p className="text-error-800">{resendError}</p>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-secondary-900">{t('auth.verificationPending.nextStepsTitle')}</h3>
              <p className="text-secondary-600">{t('auth.verificationPending.nextStepsDesc')}</p>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {/* Email Display */}
                <div className="p-4 bg-secondary-50 rounded-lg">
                  <p className="text-sm text-secondary-600 mb-1">{t('auth.verificationPending.sentTo')}</p>
                  <p className="font-medium text-secondary-900">{email}</p>
                </div>

                {/* Steps */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary-700">1</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{t('auth.verificationPending.step1Title')}</p>
                      <p className="text-xs text-secondary-600">{t('auth.verificationPending.step1Desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary-700">2</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{t('auth.verificationPending.step2Title')}</p>
                      <p className="text-xs text-secondary-600">{t('auth.verificationPending.step2Desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary-700">3</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{t('auth.verificationPending.step3Title')}</p>
                      <p className="text-xs text-secondary-600">{t('auth.verificationPending.step3Desc')}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-4 border-t border-secondary-200">
                  <button
                    onClick={handleResendVerification}
                    className="btn-primary w-full flex items-center justify-center space-x-2"
                    disabled={resendLoading}
                  >
                    {resendLoading ? (
                      <>
                        <div className="w-4 h-4 spinner"></div>
                        <span>{t('auth.emailVerification.sending')}</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        <span>{t('auth.emailVerification.resendButton')}</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleGoToLogin}
                    className="btn-outline w-full flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                    <span>{t('auth.emailVerification.backToLogin')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-secondary-500">
              {t('auth.verificationPending.didNotReceive')}
              <button
                onClick={handleResendVerification}
                className="text-primary-600 hover:text-primary-700 font-medium"
                disabled={resendLoading}
              >
                {t('auth.verificationPending.resendLink')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationPendingPage;

